// purpose: R2 全画面ポスト処理 — 中心からの放射状色収差（インパクトで脈動）と隅のビネット。バンドを含むフレーム全体に一度だけ掛ける。

import { Filter, GlProgram } from 'pixi.js';
import filterVertex from '../../../shaders/filterVertex.glsl?raw';

/**
 * One full-screen pass, two effects that both want the frame's centre: a radial
 * RGB split whose width is driven per frame by the impact envelope, and a static
 * corner vignette. They share a pass because they share the same `fromCentre`
 * vector — splitting them would cost a second render target for one `length()`.
 *
 * Two facts make the shader's frame mapping safe. The filter declares zero
 * padding, and the host pins `filterArea` to the logical canvas, so the input
 * frame is exactly R2_WIDTH x R2_HEIGHT and the caller can hand those dimensions
 * over once at construction instead of re-reading `input.frame` every frame.
 *
 * GLSL ES 3.00 style (`in`/`out`/`texture`), matching the shared vertex shader
 * this reuses from src/game/shaders. WebGL only — the R2 host requests
 * `preference: ['webgl']`, so no WGSL twin is declared.
 */

/** Where the vignette starts and where it saturates, as a radius in aspect-corrected frame units. */
const VIGNETTE_INNER_RADIUS = 0.3;
const VIGNETTE_OUTER_RADIUS = 0.62;

const IMPACT_FRAGMENT = `precision highp float;
in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform vec4 uInputSize;
uniform vec2 uDimensions;
uniform float uStrength;
uniform float uVignette;

const float VIGNETTE_INNER = ${VIGNETTE_INNER_RADIUS.toFixed(3)};
const float VIGNETTE_OUTER = ${VIGNETTE_OUTER_RADIUS.toFixed(3)};

void main(void) {
  vec4 source = texture(uTexture, vTextureCoord);
  // 0..1 across the filtered frame, whatever size the pooled input texture is.
  vec2 frameCoord = vTextureCoord * uInputSize.xy / uDimensions;
  vec2 fromCentre = frameCoord - 0.5;
  vec3 color = source.rgb;
  if (uStrength > 0.0) {
    // Red pushed outward, blue pulled inward: the fringe is zero at the centre
    // and exactly uStrength pixels wide at the frame edge, so the effect never
    // smears what the player is aiming at.
    vec2 offset = fromCentre * 2.0 * uStrength * uInputSize.zw;
    color.r = texture(uTexture, vTextureCoord + offset).r;
    color.b = texture(uTexture, vTextureCoord - offset).b;
  }
  vec2 radial = fromCentre;
  radial.y *= uDimensions.y / uDimensions.x;
  float falloff = smoothstep(VIGNETTE_INNER, VIGNETTE_OUTER, length(radial));
  color *= 1.0 - uVignette * falloff;
  finalColor = vec4(color, source.a);
}
`;

interface ImpactUniforms {
  uDimensions: Float32Array;
  uStrength: number;
  uVignette: number;
}

export class ImpactPostFilter extends Filter {
  private readonly impactUniforms: ImpactUniforms;

  constructor(frameWidth: number, frameHeight: number, vignette: number) {
    super({
      glProgram: new GlProgram({ vertex: filterVertex, fragment: IMPACT_FRAGMENT }),
      resources: {
        impactUniforms: {
          uDimensions: { value: new Float32Array([frameWidth, frameHeight]), type: 'vec2<f32>' },
          uStrength: { value: 0, type: 'f32' },
          uVignette: { value: vignette, type: 'f32' },
        },
      },
      // Zero padding is what lets the constructor's dimensions stand in for
      // `input.frame`: the pass must not quietly grow its own region.
      padding: 0,
    });
    this.impactUniforms = this.resources.impactUniforms.uniforms as ImpactUniforms;
  }

  /** Chromatic split at the frame edge, in logical pixels. A uniform write, not an allocation. */
  setStrength(pixels: number): void {
    this.impactUniforms.uStrength = pixels;
  }
}
